import { EV } from '../core/eventbus.js';
import { Repo } from '../data/schema.js';
import { getItem } from '../data/items.js';
import { gotoPortal } from '../data/world.js';
import { Loot } from './loot.js';
import { Shops } from './shops.js';
import { Avatar } from './avatar.js';
import { Jobs } from './jobs.js';
import { Assemble } from './assemble.js';
import { Vehicle } from './vehicle.js';
import { Tags } from './tags.js';
import { Inspire } from './inspire.js';
import { RECIPES } from '../data/cooking.js';
import { PILLS } from '../data/alchemy.js';
import { seasonAt } from './time.js';

/** 物品量词：把「翻出一个XX」这句话说顺 */
function unitOf(id) {
  const special = {
    engine: '台还有气的引擎', tire: '条旧轮胎', frame: '副车架子', bolt: '颗卷了牙的螺栓',
    battery: '块电瓶', seat_part: '把塌了半边的座椅', fuel: '壶灯油',
    scrap_iron: '块废铁', scrap_wood: '块废木料', scrap_cloth: '把碎布头',
    wood_scrap: '根还算规整的木料', plate_scrap: '块铁皮',
  };
  return special[id] || `些${getItem(id).name}`;
}

/**
 * 行为系统：把「交互事件」翻译成对数据库与世界的具体改动。
 * 所有玩法结果都落在 DB 上，渲染层只读，不持有状态。
 */

const MIN_GAP = 0.25; // 秒，防连点

export function installActions(engine) {
  const { events, db } = engine;
  let last = -999;

  function toast(text, kind = 'info') {
    events.emit(EV.TOAST, { text, kind });
    Repo.log(db, text, kind, Repo.getWorld(db).day, Repo.getWorld(db).minute);
  }

  function clamp(v, a = 0, b = 100) { return Math.max(a, Math.min(b, v)); }

  function bump(patch, msg, kind) {
    const p = Repo.getPlayer(db);
    Repo.savePlayer(db, patch);
    if (msg) toast(msg, kind);
  }

  /** 屋里的米缸：灶上缺米时来这儿舀（存粮那一环就接在这儿） */
  function riceBinHere() {
    const list = (engine.scene && engine.scene.props) || [];
    return list.find((x) => x.type === 'rice_bin') || null;
  }

  /** 取料：身上不够，就去屋里的米缸舀 */
  function takeWithBin(id, n, bin) {
    const own = Repo.countItem(db, id);
    if (own >= n) { Repo.takeItem(db, id, n); return; }
    if (own > 0) Repo.takeItem(db, id, own);
    const lack = n - own;
    if (id === 'rice' && bin && ((bin.state && bin.state.rice) || 0) >= lack) {
      bin.state = { ...bin.state, rice: (bin.state.rice || 0) - lack };
      db.table('props').update(bin.id, { state: bin.state });
    }
  }

  /** 灶上什么也做不出来：先给条活路（饿极了生啃青菜），再把缺什么、去哪儿弄说明白 */
  function cookEmpty(p) {
    if ((p.satiety || 0) <= 22 && Repo.countItem(db, 'veg') >= 1) {
      Repo.takeItem(db, 'veg', 1);
      Repo.savePlayer(db, { satiety: clamp((p.satiety || 0) + 7), mood: clamp((p.mood || 0) - 2) });
      return toast('锅里没米，生啃了两把青菜，涩得直皱眉——总算压住了心慌。', 'warn');
    }
    const WHERE = {
      rice: '米——早点铺四钱一升（只开到午后），或自己插一畦稻舂',
      veg: '青菜——菜畦里收，河沿上也能采',
      commonFish: '鱼——河沿下钩',
      water: '水——院里水缸、井边都成',
      herb: '草药——河沿与坡上采',
    };
    const lack = [];
    for (const [id, tip] of Object.entries(WHERE)) {
      if (Repo.countItem(db, id) <= 0) lack.push(tip);
    }
    return toast(`灶上空着：${lack.join('；')}。`, 'warn');
  }

  const handlers = {
    sleep() {
      const w = Repo.getWorld(db);
      const p = Repo.getPlayer(db);
      Repo.saveWorld(db, { day: w.day + 1, minute: 6 * 60, dayCount: w.dayCount + 1 });
      Repo.savePlayer(db, {
        day: w.day + 1,
        minute: 6 * 60,
        energy: 100,
        satiety: clamp(p.satiety - 25),
        mood: clamp(p.mood + 10),
      });
      toast('一夜过去，天光大亮。', 'good');
      events.emit(EV.TIME_NEWDAY, { day: w.day + 1 });
    },

    /**
     * 灶上：料齐了就做，做出来趁热吃。
     * 挑「做得起的里最讲究的一道」——讲究的菜还得手上有那张灶上的照。
     * 身上没米但缸里有，会自己去米缸舀（米缸是存粮那一环）。
     * 火候不够就失手，效果是半份，但经验照攒。
     */
    cook() {
      const p = Repo.getPlayer(db);
      const pk = Inspire.perks(db, 'cook');
      const bin = riceBinHere();
      const inBin = (id) => (id === 'rice' && bin ? ((bin.state && bin.state.rice) || 0) : 0);
      const canDo = RECIPES.filter((r) => (
        Object.entries(r.need).every(([id, n]) => Repo.countItem(db, id) + inBin(id) >= n)
        && (!r.qi || (p.qi || 0) >= r.qi)
        && (!r.cert || pk.unlock.includes(r.id))       // 没那张照，讲究的做不出来
      ));
      if (!canDo.length) return cookEmpty(p);
      const rec = canDo[canDo.length - 1];

      for (const [id, n] of Object.entries(rec.need)) takeWithBin(id, n, bin);
      if (rec.qi) Repo.savePlayer(db, { qi: Math.max(0, (p.qi || 0) - rec.qi) });

      const bad = Math.random() < Inspire.cookOdds(db, rec).fail;
      const k = (bad ? 0.5 : 1) * (pk.cookEffect || 1);   // 灶上的照：下肚更受用
      const eff = rec.effect || {};
      const patch = {};
      if (eff.satiety) patch.satiety = clamp(p.satiety + eff.satiety * k);
      if (eff.energy) patch.energy = clamp(p.energy + eff.energy * k);
      if (eff.mood) patch.mood = clamp(p.mood + eff.mood * k - (bad ? 1 : 0));
      Repo.savePlayer(db, patch);
      if (eff.qi) Inspire.addQi(db, eff.qi, events);

      // 灶头师傅的手艺：做的时候多留一份，端得出去
      if (!bad && pk.leftOver) {
        Repo.addItem(db, 'meal', 1);
        toast('照例多盛了一碗，留着待客。', 'info');
      }
      // 清心：这一整天念头更容易聚拢（灵感联动概率加成）
      if (!bad && rec.perk === 'clearHeart') {
        Repo.setFlag(db, `clearHeart:${Repo.getWorld(db).day}`, true);
      }
      Tags.gain(db, 'restaurant', bad ? 1 : 2, 'cook', 12);
      toast(bad ? rec.failText : rec.text, bad ? 'warn' : 'good');
    },

    draw() {
      if (Repo.countItem(db, 'water') >= 9) return toast('水已经够用了。', 'warn');
      Repo.addItem(db, 'water', 1);
      toast('取了清水一瓢。', 'info');
    },

    read() {
      const p = Repo.getPlayer(db);
      // 读书人的照：读一页多长一点灵气
      const q = 1 + (Inspire.perks(db, 'scholar').qiBonus || 0);
      Repo.savePlayer(db, { mood: clamp(p.mood + 6), qi: clamp(p.qi + q, 0, 999), energy: clamp(p.energy - 2) });
      Inspire.addQi(db, q, events);
      Tags.gain(db, 'learned', 1, 'read', 8);
      Tags.gain(db, 'insight', 1, 'read', 8);
      toast('翻了几页，心静了些。', 'good');
    },

    sit() {
      const p = Repo.getPlayer(db);
      Repo.savePlayer(db, { mood: clamp(p.mood + 2), energy: clamp(p.energy + 3) });
      toast('坐了一会儿。', 'info');
    },

    meditate() {
      const p = Repo.getPlayer(db);
      if (p.energy < 10) return toast('太累了，先歇歇吧。', 'warn');
      // 饿着也坐得住，只是气聚不拢——原来「饿到 0 就打不了坐」会把人卡死在蒲团上
      const hungry = (p.satiety || 0) <= 0;
      const q = Math.max(1, Math.round((6 + (Inspire.perks(db, 'scholar').qiBonus || 0)) * (hungry ? 0.5 : 1)));
      Repo.savePlayer(db, { qi: clamp(p.qi + q, 0, 999), energy: clamp(p.energy - 6), mood: clamp(p.mood + 4) });
      Inspire.addQi(db, q, events);
      Tags.gain(db, 'insight', 2, 'meditate', 10);
      toast(hungry ? `肚子空着，气只聚了一半（灵气 +${q}）。找口吃的再来坐。` : '吐纳片刻，灵气微增。', hungry ? 'warn' : 'good');
    },

    light(prop) {
      prop.state = { ...(prop.state || {}), lit: !(prop.state && prop.state.lit) };
      db.table('props').update(prop.id, { state: prop.state });
      toast(prop.state.lit ? '灯亮了。' : '灯灭了。', 'info');
    },

    forage(prop) {
      const roll = Math.random();
      if (roll < 0.45) { Repo.addItem(db, 'veg', 1); toast('摘到一把青菜。', 'good'); }
      else if (roll < 0.8) { Repo.addItem(db, 'herb', 1); toast('采到一些野草。', 'info'); }
      else { Repo.addItem(db, 'wood', 1); toast('折下一根枯枝。', 'info'); }
      const p = Repo.getPlayer(db);
      Repo.savePlayer(db, { energy: clamp(p.energy - 3) });
      Tags.gain(db, 'farming', 1, 'forage', 10);
    },

    /**
     * 料理一畦地：播种 → 浇水 → 收获。
     * 菜畦出青菜，水田出新谷（新谷得在米缸上舂过才是米，见 granary）——
     * 米的来路就是这么合上的：谷种 → 水田 → 新谷 → 米缸 → 灶上。
     * 多出来一路：苗在地里且今天浇过水了，再按一次 E 就是「以气催之」——
     * 花灵气把它往前推一段（修行与种地在这一处接上了）。
     */
    tend(prop) {
      const paddy = prop.type === 'paddy_plot';
      const seedId = paddy ? 'rice_seed' : 'seed';
      const seedName = paddy ? '谷种' : '菜种';
      const outId = paddy ? 'paddy' : 'veg';
      const st = { stage: 0, watered: false, ...(prop.state || {}) };
      if (st.watered && st.stage >= 1 && st.stage < 3) {
        const res = Inspire.channel(db, prop);
        return toast(res.ok ? res.text : res.reason, res.ok ? 'good' : 'warn');
      }
      if (st.stage === 0) {
        if (seasonAt(Repo.getWorld(db).day) === 'winter') return toast('地里歇着呢，开春再下种。', 'warn');
        if (Repo.countItem(db, seedId) < 1) {
          return toast(paddy ? '没有谷种了，杂货铺五钱一包。' : '没有菜种了。', 'warn');
        }
        Repo.takeItem(db, seedId, 1);
        prop.state = { stage: 1, watered: false };
        db.table('props').update(prop.id, { state: prop.state });
        Tags.gain(db, 'farming', 1, 'sow', 12);
        return toast(paddy ? '把秧插下了。' : '播下菜种。', 'good');
      }
      if (st.stage >= 3) {
        // 出：夏天多收一把，冬天勉强一把；地里的照再多给一把
        const base = { spring: 2, summer: 3, autumn: 2, winter: 1 }[seasonAt(Repo.getWorld(db).day)] || 2;
        const n = base + (Inspire.perks(db, 'farm').yield || 0);
        Repo.addItem(db, outId, n);
        // 出里有进：会伺弄的人收菜割稻时留得住种（farm_1 起）
        const seeded = Math.random() < (Inspire.perks(db, 'farm').seedRate || 0);
        if (seeded) Repo.addItem(db, seedId, 1);
        prop.state = { stage: 0, watered: false };
        db.table('props').update(prop.id, { state: prop.state });
        const p = Repo.getPlayer(db);
        Repo.savePlayer(db, { mood: clamp(p.mood + 5) });
        Tags.gain(db, 'farming', 2, 'harvest', 12);
        const tail = seeded ? `，还留下一把${seedName}` : '';
        return toast(
          paddy ? `割了${n}把新谷${tail}——舂过才是米。` : `收了${n}把青菜${tail}。`,
          'good',
        );
      }
      if (st.watered) return toast('今天已经浇过水了。', 'warn');
      prop.state = { ...st, watered: true };
      db.table('props').update(prop.id, { state: prop.state });
      const p = Repo.getPlayer(db);
      Repo.savePlayer(db, { energy: clamp(p.energy - 2) });
      Tags.gain(db, 'farming', 1, 'water', 12);
      toast(`浇了水，明日再看。${paddy ? '（割下来是新谷，得舂）' : ''}（灵气够的话，再按一次 E 能以气催之）`, 'info');
    },

    /**
     * 米缸：舂米、存米、取米。
     * 按一次做一件事，有个先后：手上有新谷就先舂，缸里有米就舀出来带着，
     * 缸底朝天就明说去哪儿籴——省得人站在灶前对着空锅发愣。
     */
    granary(prop) {
      const st = { rice: 0, ...(prop.state || {}) };
      const p = Repo.getPlayer(db);
      // 一、手上有新谷：舂成米，进缸
      if (Repo.countItem(db, 'paddy') >= 1) {
        if ((p.energy || 0) < 3) return toast('胳膊抬不起来了，歇歇再舂。', 'warn');
        Repo.takeItem(db, 'paddy', 1);
        const n = 1 + (Math.random() < 0.35 ? 1 : 0);     // 一把谷，舂出一升米，手顺时多出半升
        prop.state = { ...st, rice: (st.rice || 0) + n };
        db.table('props').update(prop.id, { state: prop.state });
        Repo.savePlayer(db, { energy: clamp(p.energy - 3) });
        Tags.gain(db, 'farming', 1, 'mill', 12);
        return toast(`舂了${n}升米进缸，缸里共${prop.state.rice}升。`, 'good');
      }
      // 二、缸里有米：舀出来带在身上（灶上缺米时也会自己来舀）
      if ((st.rice || 0) > 0) {
        const take = Math.min(st.rice, 3);
        prop.state = { ...st, rice: (st.rice || 0) - take };
        db.table('props').update(prop.id, { state: prop.state });
        Repo.addItem(db, 'rice', take);
        return toast(`从缸里舀了${take}升米，缸里还剩${prop.state.rice}升。`, 'info');
      }
      // 三、空缸：指一条明路，别让人干站着
      return toast('缸底朝天。籴米去早点铺（四钱一升，只开到午后）；要自己种，得先有谷种和一畦水田。', 'warn');
    },

    /**
     * 丹炉：草药与灵气的去处。
     * 修行不到「感气」，丹火根本点不着——这是修行第二桩用处。
     */
    refine() {
      const p = Repo.getPlayer(db);
      const canDo = PILLS.filter((pill) => (
        Object.entries(pill.need).every(([id, n]) => Repo.countItem(db, id) >= n)
      ));
      if (!canDo.length) return toast('炉子里没料可炼，先去采些草药。', 'warn');
      const pill = canDo[canDo.length - 1];
      const chk = Inspire.canRefine(db, pill);
      if (!chk.ok) return toast(chk.reason, 'warn');

      for (const [id, n] of Object.entries(pill.need)) Repo.takeItem(db, id, n);
      Repo.savePlayer(db, { qi: Math.max(0, (p.qi || 0) - (pill.qi || 0)) });

      const bad = Math.random() < Inspire.pillOdds(db, pill).fail;
      const k = bad ? 0.4 : 1;
      const eff = pill.effect || {};
      const patch = {};
      if (eff.satiety) patch.satiety = clamp(p.satiety + eff.satiety * k);
      if (eff.energy) patch.energy = clamp(p.energy + eff.energy * k);
      if (eff.mood) patch.mood = clamp(p.mood + eff.mood * k - (bad ? 1 : 0));
      if (eff.fatigue) patch.fatigue = clamp((p.fatigue || 0) + eff.fatigue * k);
      Repo.savePlayer(db, patch);
      if (eff.qi) Inspire.addQi(db, eff.qi * k, events);
      // 通脉丹：比一碗清心汤更管用——这一整天念头格外聚拢
      if (!bad && pill.perk === 'clearHeartStrong') {
        Repo.setFlag(db, `clearHeart:${Repo.getWorld(db).day}`, true);
      }
      Tags.gain(db, 'insight', bad ? 1 : 2, 'refine', 10);
      Tags.gain(db, 'farming', 1, 'refine', 10);
      toast(bad ? pill.failText : pill.text, bad ? 'warn' : 'good');
    },

    /** 照心镜：修行到了「感气」才照得见自己——修行给的唯一回报是「看见」 */
    introspect() {
      toast(Inspire.introspect(db).join('；'), 'info');
    },

    /**
     * 施饭桌：把吃食端出去。
     * 出 -> 进：一碗饭换回街坊的信任，而信任又让念头更容易撞到一处；
     * 过些日子，门口还会有人放点东西（见 systems/neighbor.js 的回礼）。
     */
    serve() {
      const w = Repo.getWorld(db);
      if (Repo.getFlag(db, `served:${w.day}`)) return toast('今天已经端出去过了，明日再说。', 'warn');
      let how = '';
      if (Repo.countItem(db, 'meal') >= 1) { Repo.takeItem(db, 'meal', 1); how = '一碗热饭'; }
      else if (Repo.countItem(db, 'veg') >= 2) { Repo.takeItem(db, 'veg', 2); how = '两把青菜'; }
      else return toast('桌上没东西可端，先做点吃的再来说这话。', 'warn');

      Repo.setFlag(db, `served:${w.day}`, true);
      Repo.setFlag(db, `gift:${w.day + 1}`, true);     // 明日门口有回礼
      const p = Repo.getPlayer(db);
      Repo.savePlayer(db, { mood: clamp(p.mood + 3) });
      Tags.gain(db, 'aid', 2, 'serve', 12);
      Tags.gain(db, 'trustOfLocals', 2, 'serve', 12);
      Tags.gain(db, 'face', 1, 'serve', 12);
      toast(`${how}搁在桌上，谁饿了谁来端。`, 'good');
    },

    store() {
      events.emit(EV.DIALOG, { kind: 'bag' });
    },

    watch(prop) {
      // 电视画面交给 HUD 面板处理（src/ui/tv.js），这里只负责开门
      events.emit(EV.TV_OPEN, { prop });
    },

    notes(prop) {
      // 白板：日记与灵感收集器（src/ui/whiteboard.js），这里只开门
      events.emit(EV.NOTES_OPEN, { prop });
    },

    bookshelf(prop) {
      // 书架面板在 src/ui/bookshelf.js，这里只开门，顺带给一点心境
      const p = Repo.getPlayer(db);
      Repo.savePlayer(db, { mood: clamp(p.mood + 2) });
      events.emit(EV.SHELF_OPEN, { prop });
    },

    rename(prop) {
      const name = window.prompt('给这块木牌题字（最多 3 字）', (prop.state && prop.state.text) || '洞天');
      if (name === null) return;
      prop.state = { ...(prop.state || {}), text: String(name).slice(0, 3) };
      db.table('props').update(prop.id, { state: prop.state });
      Tags.gain(db, 'wellSpoken', 1, 'rename', 8);   // 写在人手能看见的地方，就算会说话
      toast(`木牌上写着：${prop.state.text}`, 'info');
    },

    /* ---------------- 出门、搜刮与攒车 ---------------- */

    goto(prop) {
      const st = (prop && prop.state) || {};
      if (Vehicle.isDriving()) return toast('车过不去，先下来走路。', 'warn');
      if (!st.to) return toast('这块界石还没定去处。', 'warn');
      if (!gotoPortal(engine, prop)) toast(`没有「${st.to}」这个地方。`, 'warn');
      else if (st.label || st.to) toast(`前往${st.label || st.to}。`, 'info');
    },

    scavenge(prop) {
      const w = Repo.getWorld(db);
      const st = prop.state || {};
      // 有货单的容器走 loot 系统（这一类的垃圾、脏与味都在那儿算）
      if (st.parent) {
        const res = Loot.scavenge(db, prop);
        if (!res.ok) return toast(res.reason || '没翻到东西。', 'warn');
        return toast(res.text, 'good');
      }
      if (st.day === w.day) return toast('这里今天翻过了，明日再来。', 'warn');
      const p = Repo.getPlayer(db);
      if (p.energy < 5) return toast('累得手都抬不起来，明天再翻吧。', 'warn');

      const level = Math.max(1, Math.min(5, Number(st.level) || 1));
      const pool = [
        ['scrap_iron', 34], ['scrap_wood', 26], ['scrap_cloth', 18],
        ['bolt', 10], ['wood_scrap', 8], ['plate_scrap', 6],
      ];
      if (level >= 2) pool.push(['tire', 5], ['frame', 2]);
      if (level >= 3) pool.push(['engine', 2], ['seat_part', 2]);
      if (level >= 4) pool.push(['battery', 2], ['fuel', 3]);

      const total = pool.reduce((s, [, v]) => s + v, 0);
      let r = Math.random() * total;
      let got = pool[0][0];
      for (const [item, weight] of pool) { r -= weight; if (r <= 0) { got = item; break; } }
      Repo.addItem(db, got, 1);
      if (Math.random() < 0.3) Repo.addItem(db, 'coin', 1 + Math.floor(Math.random() * 3 * level));

      prop.state = { ...st, day: w.day };
      db.table('props').update(prop.id, { state: prop.state });
      Repo.savePlayer(db, { energy: clamp(p.energy - 4), mood: clamp(p.mood - 1) });
      Tags.gain(db, 'industrious', 1, 'scavenge', 10);   // 肯弯腰的人，日子久了看得出
      toast(`翻出${unitOf(got)}。`, 'good');
    },

    salvage(prop) {
      const w = Repo.getWorld(db);
      const st = prop.state || {};
      if (st.day === w.day) return toast('能拆的都拆下来了，明天再来。', 'warn');
      const p = Repo.getPlayer(db);
      if (p.energy < 10) return toast('力气不够，拆不动了。', 'warn');

      const level = Math.max(1, Math.min(5, Number(st.level) || 2));
      Repo.addItem(db, 'scrap_iron', 1 + Math.floor(Math.random() * 2));
      Repo.addItem(db, 'bolt', 1);
      if (level >= 2 && Math.random() < 0.4) Repo.addItem(db, 'tire', 1);
      if (level >= 3 && Math.random() < 0.25) Repo.addItem(db, 'engine', 1);
      if (Math.random() < 0.2) Repo.addItem(db, 'fuel', 1);

      prop.state = { ...st, day: w.day, level: Math.max(1, level - 1) };
      db.table('props').update(prop.id, { state: prop.state });
      Repo.savePlayer(db, { energy: clamp(p.energy - 8), mood: clamp(p.mood - 1) });
      toast('拆下几样还能用的零件。', 'good');
    },

    /** 走到工作台 / 铁砧 / 车铺工位前：开攒车那块面板 */
    assemble(prop) {
      const scene = engine.scene ? engine.scene.id : '';
      const bench = Assemble.benchOf(scene, prop && prop.type);
      events.emit(EV.GARAGE_OPEN, { bench, propType: (prop && prop.type) || '' });
    },

    /** 加油机 / 油桶：一壶灯油进油箱 */
    refuel() {
      const res = Vehicle.refuel(db);
      toast(res.ok ? res.text : (res.text || res.reason), res.ok ? 'good' : 'warn');
    },

    /** 车旁 / 工件台上：抡扳手修车 */
    repair() {
      const res = Vehicle.repair(db);
      if (res.ok) Tags.gain(db, 'skillful', 1, 'repair', 10);   // 手巧是修出来的
      toast(res.ok ? res.text : (res.text || res.reason), res.ok ? 'good' : 'warn');
    },

    /** 柜台：交给 UI 面板，买卖都在那儿谈 */
    shop(prop) {
      const st = (prop && prop.state) || {};
      const shopId = st.shop || prop.type;
      if (!Shops.SHOPS[shopId]) {
        return toast('这儿不做买卖。', 'warn');
      }
      if (!Shops.isOpen(db, shopId)) {
        return toast(`${Shops.SHOPS[shopId].name}这会儿没开门，${Shops.openHint(shopId)}。`, 'warn');
      }
      events.emit(EV.SHOP_OPEN, { shopId, prop });
    },

    /** 洗衣撬打理：靠路边的水也能擦一擦 */
    wash(prop) {
      const hasSoap = Repo.countItem(db, 'soapNut') > 0;
      const res = Avatar.wash(db, { soap: hasSoap });
      if (!res.ok) return toast(res.reason, 'warn');
      toast(res.text, 'good');
    },

    /** 泡澡：把一身味彻底洗掉 */
    bathe(prop) {
      const scene = (Repo.getPlayer(db).scene || '');
      const price = scene === 'inn' ? 8 : 5;
      const res = Avatar.bathe(db, price);
      if (!res.ok) return toast(res.reason, 'warn');
      toast(res.text, 'good');
    },

    /** 看榜：告示与零活 */
    board(prop) {
      const p = Repo.getPlayer(db);
      events.emit(EV.BOARD_OPEN, { scene: p.scene || '', prop });
    },

    /** 衙署窗口：办证照 */
    window(prop) {
      events.emit(EV.LICENSE_OPEN, { prop });
    },

    /** 发货栈／柜房：看委托清单 */
    quest(prop) {
      events.emit(EV.BOARD_OPEN, { scene: Repo.getPlayer(db).scene || '', prop, tab: 'quests' });
    },

    /** 摊位与铺面 */
    stall(prop) {
      events.emit(EV.STALL_OPEN, { scene: Repo.getPlayer(db).scene || '', prop });
    },

    /** 短工市：雇人 */
    hire(prop) {
      events.emit(EV.HIRE_OPEN, { scene: Repo.getPlayer(db).scene || '', prop });
    },

    notice() {
      const board = [
        '城西车铺收旧零件，也收旧车架——修修补补，总能凑出个能跑的。',
        '招洗碗工一名，不管饭，日结三十文。',
        '走失黄狗一只，左耳有缺，寻回者酬二百文。',
        '明日辰时有牛车进城，搭车者至村口候着。',
        '收购青菜，三文一斤，多了不收。',
      ];
      toast(board[Math.floor(Math.random() * board.length)], 'info');
    },
  };

  events.on(EV.INTERACT_DO, ({ prop, action }) => {
    const t = engine.elapsed;
    if (t - last < MIN_GAP) return;
    last = t;
    const fn = handlers[action];
    if (!fn) {
      const def = prop ? prop.type : action;
      return toast(`「${def}」还没接上玩法。`, 'warn');
    }
    fn(prop);
    // 攒过一次就看一眼：这一下有没有让心里多出个念头（隐性成长不打招呼）
    Inspire.poke(db, events);
    events.emit(EV.INVENTORY_ADD, null); // 通知 HUD 刷新
  });
}
